import React from "react";
import { useNavigate } from "react-router-dom";

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate("/")} className="flex items-center">
            <h1 className="text-2xl font-bold" style={{ color: '#30853A' }}>Plexys</h1>
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

        <div className="prose prose-lg max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction and Scope</h2>
            <p className="text-gray-700 mb-3">
              This Privacy Policy ("Policy") governs the collection, use, processing, storage, disclosure, and protection
              of information by Plexys Limited and its affiliates ("Plexys," "we," "our," or "us") in connection with
              the Plexys referral management and market analysis platform and related services (collectively, the "Service").
            </p>
            <p className="text-gray-700 mb-3">
              By accessing or using the Service, you ("User," "you," or "your") acknowledge that you have read, understood,
              and agree to be bound by this Policy. If you do not agree with the terms of this Policy, you must immediately
              discontinue use of the Service.
            </p>
            <p className="text-gray-700">
              This Policy applies to all users of the Service, including healthcare providers, practice administrators,
              and any authorized personnel accessing the Service on behalf of a healthcare entity. This Policy should be
              read in conjunction with our Terms of Service, which govern your use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Categories of Information Collected</h2>
            <p className="text-gray-700 mb-3">
              We collect and process the following categories of information in connection with your use of the Service:
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">2.1 Account and Registration Information</h3>
            <p className="text-gray-700 mb-2">
              When you create an account, we collect:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Full name and professional credentials</li>
              <li>Email address</li>
              <li>Password (stored using industry-standard cryptographic hashing)</li>
              <li>Practice name and location (city)</li>
              <li>Account creation timestamp</li>
              <li>Legal agreement acceptance records (Privacy Policy and Terms of Service acceptance with timestamps)</li>
              <li>Professional verification information, if applicable</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">2.2 De-Identified Referral Data</h3>
            <p className="text-gray-700 mb-3">
              The Service is specifically designed to minimize the collection and storage of Protected Health Information
              ("PHI") as defined under the Health Insurance Portability and Accountability Act of 1996 ("HIPAA") and its
              implementing regulations. We collect only the following de-identified referral metadata:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Referring physician name, National Provider Identifier (NPI), and practice information</li>
              <li>Patient geographic location (ZIP code only - no street addresses)</li>
              <li>Insurance payor type (normalized categories, no policy numbers or member IDs)</li>
              <li>Referral quality assessment scores (user-generated ratings on a standardized scale)</li>
              <li>System-generated unique patient identifiers (no Medical Record Numbers, patient names, dates of birth,
                  diagnoses, treatment information, or any other PHI)</li>
              <li>Referral creation and modification timestamps</li>
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>Important Notice:</strong> Users are expressly prohibited from entering PHI into the Service,
              including but not limited to patient names, Medical Record Numbers, Social Security Numbers, dates of birth,
              diagnoses, treatment plans, clinical notes, or any other information that could be used to identify an
              individual patient. Violation of this prohibition may result in immediate account termination.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">2.3 Technical and Usage Data</h3>
            <p className="text-gray-700 mb-2">
              We automatically collect certain technical information when you access the Service:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Internet Protocol (IP) address and geolocation data derived from IP address</li>
              <li>Device information (device type, operating system, browser type and version)</li>
              <li>Session information (login timestamps, session duration, authentication tokens)</li>
              <li>Usage patterns and feature utilization metrics</li>
              <li>Error logs, crash reports, and diagnostic information</li>
              <li>Performance data and system health metrics</li>
              <li>Referrer URLs and navigation paths within the Service</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">2.4 Communications and Support Data</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Customer support inquiries and correspondence</li>
              <li>Feedback, survey responses, and testimonials</li>
              <li>Email communications and notification preferences</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Lawful Bases and Purposes for Processing</h2>
            <p className="text-gray-700 mb-3">
              We process your information only for legitimate business purposes and on the following lawful bases:
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.1 Contract Performance</h3>
            <p className="text-gray-700 mb-2">
              To fulfill our contractual obligations to you and provide the Service, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Creating, maintaining, and authenticating your account</li>
              <li>Processing and storing referral data you input into the Service</li>
              <li>Generating analytics, reports, and market insights based on your data</li>
              <li>Providing customer support and responding to your inquiries</li>
              <li>Processing payments and maintaining billing records</li>
              <li>Communicating with you about the Service, including service updates and maintenance</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.2 Legitimate Interests</h3>
            <p className="text-gray-700 mb-2">
              To pursue our legitimate business interests, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Improving, optimizing, and developing new features for the Service</li>
              <li>Conducting research and statistical analysis on de-identified, aggregated data</li>
              <li>Detecting, preventing, and investigating security incidents, fraud, and abuse</li>
              <li>Protecting our legal rights and property</li>
              <li>Conducting internal business operations and quality assurance</li>
              <li>Analyzing usage patterns to enhance user experience</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.3 Legal Compliance</h3>
            <p className="text-gray-700 mb-2">
              To comply with applicable laws, regulations, legal processes, or governmental requests, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Responding to subpoenas, court orders, or other legal processes</li>
              <li>Cooperating with law enforcement or regulatory investigations</li>
              <li>Maintaining records required by applicable laws and regulations</li>
              <li>Enforcing our Terms of Service and other legal agreements</li>
              <li>Protecting against legal liability</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.4 Consent</h3>
            <p className="text-gray-700">
              Where you have provided explicit consent, we may use your information for additional purposes such as
              marketing communications, product announcements, and participation in research studies. You may withdraw
              your consent at any time by contacting us or using the opt-out mechanisms provided in our communications.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. HIPAA Compliance and Healthcare Privacy</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.1 Service Design and PHI Minimization</h3>
            <p className="text-gray-700 mb-3">
              The Service has been intentionally designed and architected to minimize the collection, use, storage, and
              transmission of Protected Health Information ("PHI") as defined under the Health Insurance Portability and
              Accountability Act of 1996 ("HIPAA"), 45 C.F.R. Parts 160 and 164. Specifically:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>The Service does not collect, store, or process patient names, Medical Record Numbers (MRNs), Social
                  Security Numbers, dates of birth, diagnoses, treatment plans, clinical notes, medical records, or any
                  other PHI</li>
              <li>Only de-identified, aggregated referral metadata is retained, consistent with HIPAA's de-identification
                  standards under 45 C.F.R. § 164.514</li>
              <li>The Service employs system-generated unique identifiers that cannot be reverse-engineered to identify
                  individual patients</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.2 User Responsibilities and Prohibited Uses</h3>
            <p className="text-gray-700 mb-3">
              <strong>CRITICAL NOTICE:</strong> Users are solely and exclusively responsible for ensuring their use of
              the Service complies with HIPAA, the HITECH Act, state privacy laws, and all other applicable healthcare
              privacy and security regulations. Users expressly covenant and agree to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>NOT</strong> enter, upload, transmit, or store any PHI in the Service</li>
              <li><strong>NOT</strong> enter any information that could be used alone or in combination with other
                  information to identify an individual patient</li>
              <li>Implement and maintain appropriate administrative, physical, and technical safeguards in their own
                  practices to protect patient information</li>
              <li>Train all personnel with access to the Service on proper use and HIPAA compliance</li>
              <li>Immediately report any suspected security incidents or PHI disclosures to Plexys</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.3 No Business Associate Relationship</h3>
            <p className="text-gray-700 mb-3">
              Because the Service is designed to avoid PHI, Plexys does not act as a "Business Associate" as defined
              under HIPAA. No Business Associate Agreement is required or entered into. Users acknowledge that Plexys
              has no access to, creates no, receives no, maintains no, and transmits no PHI on behalf of any covered
              entity.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.4 Indemnification for PHI Violations</h3>
            <p className="text-gray-700">
              Users agree to indemnify, defend, and hold harmless Plexys from any and all claims, damages, liabilities,
              costs, and expenses (including reasonable attorneys' fees) arising from or related to User's unauthorized
              entry of PHI into the Service or violation of HIPAA or other healthcare privacy laws in connection with
              their use of the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Information Sharing, Disclosure, and Third Parties</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.1 No Sale of Personal Information</h3>
            <p className="text-gray-700 mb-3">
              Plexys does not and will not sell, rent, lease, or otherwise commercially exploit your personal information
              to third parties for monetary or other valuable consideration.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.2 Service Providers and Subprocessors</h3>
            <p className="text-gray-700 mb-3">
              We may share your information with carefully vetted third-party service providers who perform services on
              our behalf under contractual obligations to maintain confidentiality and security, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Cloud infrastructure and hosting providers (e.g., AWS, Google Cloud, Azure)</li>
              <li>Database management and backup services</li>
              <li>Customer support and communication platforms</li>
              <li>Analytics and performance monitoring services</li>
              <li>Payment processing providers</li>
              <li>Security and fraud prevention services</li>
            </ul>
            <p className="text-gray-700 mt-3">
              All service providers are bound by confidentiality obligations and data processing agreements that
              prohibit them from using your information for any purpose other than providing services to Plexys.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.3 Legal Compliance and Law Enforcement</h3>
            <p className="text-gray-700 mb-3">
              We may disclose your information when required by law or in response to valid legal processes, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Subpoenas, court orders, or other judicial or administrative proceedings</li>
              <li>Law enforcement requests or investigations</li>
              <li>National security requirements</li>
              <li>Regulatory audits or investigations by healthcare or government authorities</li>
              <li>Legal obligations to report suspected fraud, abuse, or unlawful activity</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.4 Business Transfers</h3>
            <p className="text-gray-700 mb-3">
              In the event of a merger, acquisition, reorganization, bankruptcy, or sale of assets, your information
              may be transferred to the acquiring entity. We will provide notice before your information is transferred
              and becomes subject to a different privacy policy. The acquiring entity will be required to honor the
              terms of this Policy or obtain your consent for any material changes.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.5 Protection of Rights and Safety</h3>
            <p className="text-gray-700 mb-3">
              We may disclose your information when we believe in good faith that disclosure is necessary to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Protect and defend our legal rights, property, or safety</li>
              <li>Protect the rights, property, or safety of our users or the public</li>
              <li>Investigate, prevent, or take action regarding suspected or actual illegal activities, fraud,
                  security incidents, or violations of our Terms of Service</li>
              <li>Enforce our legal agreements and policies</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.6 Aggregated and De-Identified Data</h3>
            <p className="text-gray-700">
              We may share aggregated, de-identified, or anonymized information that cannot reasonably be used to
              identify you for research, analytics, marketing, or other business purposes. Such information is not
              considered personal information under this Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Information Security and Data Protection</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">6.1 Security Measures</h3>
            <p className="text-gray-700 mb-3">
              We implement and maintain reasonable administrative, technical, and physical safeguards designed to
              protect your information against unauthorized access, disclosure, alteration, and destruction, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Encryption:</strong> All data in transit is encrypted using industry-standard Transport Layer
                  Security (TLS 1.2 or higher). Sensitive data at rest is encrypted using AES-256 or equivalent
                  encryption standards.</li>
              <li><strong>Authentication:</strong> Multi-factor authentication options, secure password policies
                  (hashed using bcrypt or similar algorithms), and session management controls.</li>
              <li><strong>Access Controls:</strong> Role-based access controls (RBAC), principle of least privilege,
                  and segregation of duties to limit access to authorized personnel only.</li>
              <li><strong>Network Security:</strong> Firewalls, intrusion detection and prevention systems (IDS/IPS),
                  and regular vulnerability assessments.</li>
              <li><strong>Monitoring:</strong> Continuous security monitoring, logging, and audit trails of system
                  access and data modifications.</li>
              <li><strong>Incident Response:</strong> Documented security incident response and breach notification
                  procedures.</li>
              <li><strong>Regular Updates:</strong> Timely application of security patches and updates to all systems.</li>
              <li><strong>Employee Training:</strong> Regular security awareness training for all personnel with access
                  to user information.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">6.2 Security Limitations and Disclaimer</h3>
            <p className="text-gray-700 mb-3">
              While we implement commercially reasonable security measures, no method of electronic transmission or
              storage is completely secure. We cannot and do not guarantee absolute security of your information.
              Transmission of information via the Internet is done at your own risk.
            </p>
            <p className="text-gray-700 mb-3">
              <strong>User Responsibilities:</strong> You are responsible for maintaining the confidentiality of your
              account credentials, using strong passwords, enabling available security features (such as multi-factor
              authentication), and promptly notifying us of any unauthorized access to your account.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">6.3 Breach Notification</h3>
            <p className="text-gray-700">
              In the event of a security breach that compromises your personal information, we will notify you and
              applicable regulatory authorities in accordance with applicable laws and within the timeframes required
              by law. Notifications will include information about the nature of the breach, the types of information
              affected, steps we are taking to address the breach, and recommendations for protecting your information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Retention and Deletion</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.1 Retention Periods</h3>
            <p className="text-gray-700 mb-3">
              We retain your information for as long as necessary to fulfill the purposes described in this Policy,
              unless a longer retention period is required or permitted by law. Specifically:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Account Information:</strong> Retained for the duration of your active account plus seven
                  (7) years after account closure for legal, tax, and audit purposes.</li>
              <li><strong>Referral Data:</strong> Retained for the duration of your active account plus seven (7) years
                  for regulatory compliance and dispute resolution.</li>
              <li><strong>Usage and Log Data:</strong> Retained for up to two (2) years for security, fraud prevention,
                  and service improvement purposes.</li>
              <li><strong>Legal Agreement Records:</strong> Retained indefinitely or as required by law to demonstrate
                  compliance with legal obligations.</li>
              <li><strong>Communications:</strong> Customer support records retained for three (3) years.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.2 Account Deletion</h3>
            <p className="text-gray-700 mb-3">
              You may request deletion of your account at any time by contacting us. Upon receiving your request, we will:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Deactivate your account within 30 days</li>
              <li>Delete or anonymize your personal information within 90 days, except as required for legal obligations</li>
              <li>Notify you when the deletion process is complete</li>
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>Note:</strong> We may retain certain information as required by law, for legitimate business
              purposes (such as fraud prevention, dispute resolution, and enforcing our agreements), or in backup
              systems for a limited period until such systems are refreshed.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.3 Legal Holds</h3>
            <p className="text-gray-700">
              If your information is subject to litigation holds, regulatory investigations, or other legal requirements,
              we may be unable to delete certain information until such holds are released.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Your Privacy Rights</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">8.1 General Rights</h3>
            <p className="text-gray-700 mb-3">
              Subject to applicable law and certain exceptions, you have the following rights regarding your personal
              information:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Right to Access:</strong> You may request access to the personal information we hold about
                  you and receive a copy in a structured, commonly used format.</li>
              <li><strong>Right to Rectification:</strong> You may request correction of inaccurate or incomplete
                  personal information.</li>
              <li><strong>Right to Deletion:</strong> You may request deletion of your personal information, subject
                  to certain legal exceptions.</li>
              <li><strong>Right to Data Portability:</strong> You may request to receive your personal information in
                  a portable format or have it transmitted directly to another service provider where technically feasible.</li>
              <li><strong>Right to Object:</strong> You may object to certain processing of your personal information,
                  including processing for direct marketing purposes.</li>
              <li><strong>Right to Restriction:</strong> You may request restriction of processing in certain circumstances,
                  such as when you contest the accuracy of your information.</li>
              <li><strong>Right to Withdraw Consent:</strong> Where processing is based on your consent, you may withdraw
                  consent at any time without affecting the lawfulness of processing prior to withdrawal.</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">8.2 Exercising Your Rights</h3>
            <p className="text-gray-700 mb-3">
              To exercise any of these rights, please contact us through the methods provided in Section 12. We will
              respond to your request within the timeframes required by applicable law (typically 30 days). We may
              require verification of your identity before processing your request.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">8.3 Right to Lodge a Complaint</h3>
            <p className="text-gray-700">
              If you believe we have violated your privacy rights or applicable data protection laws, you have the
              right to lodge a complaint with a supervisory authority in your jurisdiction. However, we encourage you
              to contact us first so we can address your concerns directly.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Third-Party Services and Integrations</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">9.1 Third-Party Data Sources</h3>
            <p className="text-gray-700 mb-3">
              The Service integrates with and retrieves data from third-party sources to provide enhanced functionality,
              including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>National Provider Identifier (NPI) Registry:</strong> Publicly available provider information
                  maintained by the Centers for Medicare & Medicaid Services (CMS).</li>
              <li><strong>CMS Provider Utilization Data:</strong> Publicly available Medicare payment and utilization data.</li>
              <li><strong>Google Places API:</strong> For provider location, contact information, and review data.</li>
              <li><strong>Mapping Services:</strong> For geographic visualization and analysis.</li>
            </ul>
            <p className="text-gray-700 mt-3">
              Your use of these third-party data sources through our Service is subject to the respective third parties'
              terms of service and privacy policies.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">9.2 Third-Party Links</h3>
            <p className="text-gray-700 mb-3">
              The Service may contain links to third-party websites, applications, or services that are not owned or
              controlled by Plexys. We are not responsible for and do not endorse the privacy practices or content of
              third-party services. We strongly encourage you to review the privacy policies of any third-party services
              you access through our Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">9.3 No Liability for Third Parties</h3>
            <p className="text-gray-700">
              Plexys shall not be liable for any acts, omissions, or privacy practices of third parties, including any
              unauthorized access to or disclosure of your information by third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700 mb-3">
              The Service is not intended for, designed for, or directed at individuals under the age of eighteen (18)
              years ("Minors"). We do not knowingly collect, use, or disclose personal information from Minors without
              verified parental consent as required by the Children's Online Privacy Protection Act ("COPPA") and other
              applicable laws.
            </p>
            <p className="text-gray-700 mb-3">
              If you are a parent or guardian and believe that your child has provided us with personal information without
              your consent, please contact us immediately using the contact information in Section 12. Upon verification,
              we will promptly delete such information from our systems.
            </p>
            <p className="text-gray-700">
              By using the Service, you represent and warrant that you are at least eighteen (18) years of age or have
              obtained verified parental consent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. International Data Transfers</h2>
            <p className="text-gray-700 mb-3">
              Your information may be transferred to, stored, and processed in countries other than your country of
              residence, including the United States, where data protection laws may differ from those in your jurisdiction.
            </p>
            <p className="text-gray-700 mb-3">
              When we transfer your information internationally, we implement appropriate safeguards to protect your
              information in accordance with this Policy and applicable laws, including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Standard contractual clauses approved by relevant regulatory authorities</li>
              <li>Data processing agreements with service providers</li>
              <li>Compliance with applicable data transfer frameworks</li>
              <li>Technical and organizational security measures</li>
            </ul>
            <p className="text-gray-700 mt-3">
              By using the Service, you consent to the transfer of your information to countries outside your country of
              residence, which may have different data protection laws.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes and Updates to This Policy</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">12.1 Right to Modify</h3>
            <p className="text-gray-700 mb-3">
              We reserve the right to modify, amend, or update this Privacy Policy at any time in our sole discretion.
              When we make changes, we will update the "Last Updated" date at the top of this Policy.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">12.2 Notice of Material Changes</h3>
            <p className="text-gray-700 mb-3">
              For material changes that significantly affect your rights or how we process your information, we will
              provide notice through one or more of the following methods:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Email notification to the address associated with your account</li>
              <li>Prominent notice within the Service or on our website</li>
              <li>In-application notification upon your next login</li>
              <li>Such other methods as may be required by applicable law</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">12.3 Acceptance of Changes</h3>
            <p className="text-gray-700">
              Your continued use of the Service after the effective date of any changes to this Policy constitutes your
              acceptance of such changes. If you do not agree to the modified Policy, you must discontinue use of the
              Service and may request deletion of your account as described in Section 7.2.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Information and Data Protection Officer</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">13.1 General Inquiries</h3>
            <p className="text-gray-700 mb-3">
              If you have any questions, concerns, or complaints about this Privacy Policy, our data practices, or wish
              to exercise your privacy rights, please contact us through the application support channels or at the
              following address:
            </p>
            <p className="text-gray-700 font-medium mb-3">
              Plexys Limited<br />
              Attention: Privacy Officer<br />
              Email: privacy@plexys.com<br />
              Response Time: We will respond to all inquiries within 30 days.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">13.2 Privacy Rights Requests</h3>
            <p className="text-gray-700 mb-3">
              To exercise your privacy rights under this Policy or applicable law, please submit a request through the
              Service or email us at privacy@plexys.com. Include your name, email address, and a detailed description
              of your request. We may require additional information to verify your identity before processing your request.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">13.3 Security Incidents</h3>
            <p className="text-gray-700">
              To report a security incident or suspected unauthorized access to your account, please contact us
              immediately at security@plexys.com.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Entire Agreement and Severability</h2>
            <p className="text-gray-700 mb-3">
              This Privacy Policy, together with our Terms of Service, constitutes the entire agreement between you and
              Plexys regarding the privacy aspects of your use of the Service and supersedes all prior or contemporaneous
              communications and proposals, whether oral or written, between you and Plexys.
            </p>
            <p className="text-gray-700">
              If any provision of this Policy is found to be invalid, illegal, or unenforceable by a court of competent
              jurisdiction, such provision shall be severed from this Policy, and the remaining provisions shall remain
              in full force and effect to the maximum extent permitted by law.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
